import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Modal,
  Backdrop,
  Fade,
} from "@mui/material";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import { Inventory2, Search } from "@mui/icons-material";
import { StockMedicineSkeleton } from "./loading/StockMedicineSkeleton";
import { useTranslation } from "react-i18next";

const tableStyle = {
  textAlign: "center",
  cursor: "pointer",
};

const StockMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const {
    data: stockMedicines = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/?search=${searchQuery}${
      categoryFilter ? `&medicine__category=${categoryFilter}` : ""
    }&ordering=${sortOrder === "asc" ? sortField : `-${sortField}`}`,
    token
  );

  const { data: medicineCategory = [] } = useFetchObjects(
    ["medicine-categories"],
    "medicine-categories/",
    token
  );

  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    refetch();
  }, [searchQuery, categoryFilter, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const totalAmount = stockMedicines.reduce((sum, stock) => {
    const quantity = parseFloat(stock.quantity) || 0;
    const sellPrice = parseFloat(stock.sell_price) || 0;
    return sum + quantity * sellPrice;
  }, 0);

  const totalPurchaseAmount = stockMedicines.reduce((sum, stock) => {
    const totalPurchasePrice =
      stock.purchase_items?.reduce(
        (itemSum, item) =>
          itemSum +
          (parseFloat(item.purchase_price) || 0) *
            (parseFloat(item.quantity) || 0),
        0
      ) || 0;
    return sum + totalPurchasePrice;
  }, 0);

  const handleDownloadPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const content = document.getElementById("stock-medicine-table");
    const canvas = await html2canvas(content, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
    pdf.save("StockMedicineReport.pdf");
  };

  const handleRowClick = (stock) => {
    setSelectedStock(stock);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedStock(null);
  };

  if (isLoading) return <StockMedicineSkeleton />;

  if (isError)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">{error.message}</Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "80vh",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: { xs: "1.5rem", md: "2.5rem" },
          boxShadow: "0px 0px 16px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          zIndex: 1,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Avatar sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}>
              <Inventory2 fontSize="large" />
            </Avatar>
          </Grid>
          <Grid item>
            <Typography fontWeight={700} variant="h4" color="primary">
              {t("pharmacy.stockMedicine")}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {t("pharmacy.overview")}
            </Typography>
          </Grid>
        </Grid>

        {/* Search & Filter */}
        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t("pharmacy.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: "#fff", borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>{t("pharmacy.filterByCategory")}</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label={t("pharmacy.filterByCategory")}
              >
                <MenuItem value="">----------------</MenuItem>
                {medicineCategory.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: { xs: "1rem", md: "2rem" },
          backgroundColor: "#f8f9fa",
        }}
      >
        <TableContainer component={Box} id="stock-medicine-table">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell style={tableStyle}>#</TableCell>
                <TableCell
                  style={tableStyle}
                  onClick={() => handleSort("medicine__category")}
                >
                  {t("pharmacy.category")}
                </TableCell>
                <TableCell
                  style={tableStyle}
                  onClick={() => handleSort("medicine__name")}
                >
                  {t("pharmacy.medicineName")}
                </TableCell>
                <TableCell
                  style={tableStyle}
                  onClick={() => handleSort("expiry_date")}
                >
                  {t("pharmacy.expiryDate")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("pharmacy.quantity")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("pharmacy.purchasePrice")} (؋)
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("pharmacy.sellPrice")} (؋)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockMedicines.map((stock, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                  onClick={() => handleRowClick(stock)}
                >
                  <TableCell style={tableStyle}>{idx + 1}</TableCell>
                  <TableCell style={tableStyle}>
                    {stock.category_display}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {stock.medicine_display}
                  </TableCell>
                  <TableCell style={tableStyle}>{stock.expiry_date}</TableCell>
                  <TableCell style={tableStyle}>{stock.quantity}</TableCell>
                  <TableCell style={tableStyle}>
                    {stock.purchase_items?.length > 0
                      ? stock.purchase_items[stock.purchase_items.length - 1]
                          .purchase_price
                      : 0}{" "}
                    ؋
                  </TableCell>
                  <TableCell style={tableStyle}>{stock.sell_price} ؋</TableCell>
                </TableRow>
              ))}

              {/* Totals */}
              <TableRow
                hover
                sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
              >
                <TableCell
                  style={{ ...tableStyle, textAlign: "right" }}
                  colSpan={6}
                >
                  <Typography fontWeight="bolder">
                    {t("pharmacy.totalValue")}
                  </Typography>
                </TableCell>
                <TableCell style={tableStyle}>
                  <Typography fontWeight="bolder">{totalAmount} ؋</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <Typography variant="h6" fontWeight={700} color="primary">
              {selectedStock?.medicine_display}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Category: {selectedStock?.category_display}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Quantity: {selectedStock?.quantity}
            </Typography>

            <Table sx={{ mt: 2 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>{t("pharmacy.purchaseDate")}</TableCell>
                  <TableCell>{t("pharmacy.quantity")}</TableCell>
                  <TableCell>{t("pharmacy.purchasePrice")} (؋)</TableCell>
                  <TableCell>{t("pharmacy.sellPrice")} (؋)</TableCell>
                  <TableCell>{t("pharmacy.expiryDate")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedStock?.purchase_items?.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.created_at}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.purchase_price} ؋</TableCell>
                    <TableCell>{item.sell_price} ؋</TableCell>
                    <TableCell>{item.expiry_date}</TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>
                    <Typography fontWeight={700}>Total</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {selectedStock?.purchase_items?.reduce(
                        (sum, it) => sum + it.quantity,
                        0
                      ) ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {selectedStock?.purchase_items?.reduce(
                        (sum, it) => sum + it.quantity * it.purchase_price,
                        0
                      ) ?? 0}{" "}
                      ؋
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {selectedStock?.purchase_items?.reduce(
                        (sum, it) => sum + it.quantity * it.sell_price,
                        0
                      ) ?? 0}{" "}
                      ؋
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {/* Optional profit column */}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Fade>
      </Modal>

      {/* Footer */}
      <Box
        sx={{
          padding: { xs: "1rem", md: "2rem" },
          boxShadow: "0px -4px 16px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadPDF}
          >
            {t("pharmacy.downloadPdf")}
          </Button>

          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ mr: 2 }}
              color="primary"
            >
              {t("pharmacy.totalPurchaseValue")}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#1976d2" }}>
              {parseFloat(totalPurchaseAmount.toFixed(2))} ؋
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ mr: 2 }}
              color="primary"
            >
              {t("pharmacy.totalValue")}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#1976d2" }}>
              {parseFloat(totalAmount.toFixed(2))} ؋
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StockMedicine;
